import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('date-desc');
  const [expandedOrder, setExpandedOrder] = useState(null); // show items for one order at a time

  // Load all orders from Firestore, newest first
  useEffect(() => {
    async function loadOrders() {
      try {
        const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        setOrders(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error loading orders:', err);
      }
      setLoading(false);
    }
    loadOrders();
  }, []);

  // Sort orders in the browser based on the dropdown selection
  const sorted = [...orders].sort((a, b) => {
    if (sortBy === 'date-desc') return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
    if (sortBy === 'date-asc') return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
    if (sortBy === 'amount-high') return (b.total || 0) - (a.total || 0);
    if (sortBy === 'amount-low') return (a.total || 0) - (b.total || 0);
    if (sortBy === 'customer') return (a.userEmail || '').localeCompare(b.userEmail || '');
    return 0;
  });

  // Format a Firestore Timestamp to a readable date string
  function formatDate(timestamp) {
    if (!timestamp?.seconds) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  // Badge color based on order status
  function statusBadge(status) {
    if (status === 'delivered') return 'bg-success';
    if (status === 'shipped') return 'bg-info text-dark';
    return 'bg-warning text-dark'; // pending
  }

  return (
    <div className="container py-5">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <Link to="/admin" className="text-muted small text-decoration-none">← Dashboard</Link>
          <h2 className="fw-bold mb-0">🧾 Orders</h2>
        </div>

        {/* Sort dropdown */}
        <select
          className="form-select w-auto"
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
        >
          <option value="date-desc">Date: Newest First</option>
          <option value="date-asc">Date: Oldest First</option>
          <option value="amount-high">Amount: High to Low</option>
          <option value="amount-low">Amount: Low to High</option>
          <option value="customer">Customer: A–Z</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: 'var(--piggy-pink)' }} role="status" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <p className="mb-0">No orders yet. Orders will appear here once customers start buying.</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Total</th>
                <th>Status</th>
                <th>Items</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(order => (
                <React.Fragment key={order.id}>
                  <tr>
                    <td
                      className="small text-muted"
                      style={{ fontFamily: 'monospace', maxWidth: '130px', wordBreak: 'break-all' }}
                    >
                      {order.id}
                    </td>
                    <td>
                      <div>{order.shipping?.name || '—'}</div>
                      <div className="text-muted small">{order.userEmail}</div>
                    </td>
                    <td className="small">{formatDate(order.createdAt)}</td>
                    <td className="fw-bold">${order.total?.toFixed(2)}</td>
                    <td>
                      <span className={`badge ${statusBadge(order.status)}`}>
                        {order.status || 'pending'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setExpandedOrder(
                          expandedOrder === order.id ? null : order.id
                        )}
                      >
                        {expandedOrder === order.id ? 'Hide' : 'View'} Items
                      </button>
                    </td>
                  </tr>

                  {/* Expandable row showing the ordered items */}
                  {expandedOrder === order.id && (
                    <tr>
                      <td colSpan="6" className="bg-light">
                        <div className="p-2">
                          <strong className="small">Order Items:</strong>
                          <ul className="mb-1 mt-1">
                            {order.items?.map(item => (
                              <li key={item.id} className="small">
                                {item.name} × {item.quantity} — ${(item.price * item.quantity).toFixed(2)}
                              </li>
                            ))}
                          </ul>
                          <div className="small text-muted">
                            Subtotal: ${order.subtotal?.toFixed(2)} |{' '}
                            {order.discountCode && `Discount (${order.discountCode}): −$${order.discountAmount?.toFixed(2)} | `}
                            Tax: ${order.tax?.toFixed(2)} |{' '}
                            <strong>Total: ${order.total?.toFixed(2)}</strong>
                          </div>
                          <div className="small text-muted">
                            Ship to: {order.shipping?.address}, {order.shipping?.city},{' '}
                            {order.shipping?.state} {order.shipping?.zip}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminOrders;
